"use client";
import useHandleSearchParams from "@/hooks/useHandleSearchParams";
import {
  useCurrentRefinements,
  useClearRefinements,
} from "react-instantsearch";
import {
  useIsResultTabView,
  useProductCreatedRange,
} from "../SearchContextProvider";
import {
  PRODUCT_TO_RESULT_TYPE_MAP,
  PRODUCT_TYPES_TO_SHOW,
  DURATION_WEEK_RANGE,
} from "@/components/Search/constants";
import getDefaultDateRange from "@/components/Search/utils/getDefaultDateRange";
import useRefinementExcludedAttributes from "../../hooks/useRefinementExcludedAttributes";
import { resolveClientMarketplaceAttribution } from "@/utils/functions/marketplaceAttributionSession";
import useTranslations from "@/i18n/useTranslations";

function SearchActiveRefinement() {
  const t = useTranslations("Common");
  const excludedAttributes = useRefinementExcludedAttributes();
  const { setIsResultTabView, isResultTabView } = useIsResultTabView();
  const { productCreatedRange, clearProductCreatedRange } =
    useProductCreatedRange();
  const { items, refine } = useCurrentRefinements({
    excludedAttributes: excludedAttributes,
  });
  const { refine: clearAllRefinements } = useClearRefinements({
    excludedAttributes: excludedAttributes,
  });
  const { removeSearchParam, searchParams } = useHandleSearchParams();
  const filterLabel = getDefaultDateRange().filterLabel;
  const hasTabParam = searchParams.tab && searchParams.tab.length > 0;
  const isWeekRangeTab =
    searchParams.tab && searchParams.tab[0] === DURATION_WEEK_RANGE
      ? true
      : false;

  if (
    items.length === 0 &&
    !(isWeekRangeTab && isResultTabView) &&
    !productCreatedRange
  ) {
    return null;
  }

  const handleClearAll = () => {
    setIsResultTabView(false);
    if (productCreatedRange) {
      clearProductCreatedRange();
    }
    const queryValue = searchParams.q ? searchParams.q[0] : null;
    const newParams = resolveClientMarketplaceAttribution(
      window.location.search,
    );
    if (queryValue) {
      newParams.set("q", queryValue);
    }
    const newQuery = newParams.toString();
    const newUrl = `${window.location.pathname}${newQuery ? `?${newQuery}` : ""}`;
    window.history.pushState(null, "", newUrl);
    clearAllRefinements();
  };

  return (
    <div className="flex flex-wrap justify-start items-center gap-y-2">
      {hasTabParam &&
        items.map((item) =>
          item.refinements.map((refinement) => (
            <div
              key={refinement.label}
              className="border border-solid rounded mr-2 flex text-xs"
            >
              <div className="py-0.5 pl-2 flex items-center">
                {refinement.label}
              </div>
              <button
                className="h-auto pb-0.5 ml-1 px-2 hover:bg-black hover:text-white"
                onClick={() => {
                  refine(refinement);
                  if (
                    PRODUCT_TYPES_TO_SHOW.includes(refinement.value.toString())
                  ) {
                    removeSearchParam(
                      "tab",
                      PRODUCT_TO_RESULT_TYPE_MAP[refinement.value.toString()],
                    );
                    setIsResultTabView(false);
                  } else {
                    removeSearchParam(
                      refinement.attribute,
                      refinement.value.toString(),
                    );
                  }
                }}
              >
                x
              </button>
            </div>
          )),
        )}
      {hasTabParam && isWeekRangeTab && (
        <div className="border border-solid rounded mr-2 flex text-xs">
          <div className="py-0.5 pl-2 flex items-center">{t("weekRange")}</div>
          <button
            className="h-auto pb-0.5 ml-1 px-2 hover:bg-black hover:text-white"
            onClick={() => {
              removeSearchParam("tab", DURATION_WEEK_RANGE);
              setIsResultTabView(false);
            }}
          >
            x
          </button>
        </div>
      )}
      {productCreatedRange && (
        <div className="border border-solid rounded mr-2 flex text-xs">
          <div className="py-0.5 pl-2 flex items-center">{filterLabel}</div>
          <button
            className="h-auto pb-0.5 ml-1 px-2 hover:bg-black hover:text-white"
            onClick={clearProductCreatedRange}
          >
            x
          </button>
        </div>
      )}
      <button
        className="ml-2 text-xs text-gray-700 underline hover:text-link-hover"
        onClick={handleClearAll}
      >
        Clear All
      </button>
    </div>
  );
}

export default SearchActiveRefinement;
